import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { captureLeadEmail } from '../utils/captureLeadEmail';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import PageSEO from './PageSEO';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [resendInfo, setResendInfo] = useState(null);
  const [resendStatus, setResendStatus] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);

  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const redirectTarget = redirectParam && redirectParam.startsWith('/') ? redirectParam : null;
  const reason = new URLSearchParams(location.search).get('reason');

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleEmailBlur = () => {
    captureLeadEmail(email, window.location.pathname, { context: 'login' });
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError(null);
    setGoogleLoading(true);
    try {
      // Pass userType hint from URL params (e.g., from tenant invite redirect)
      const loginParams = new URLSearchParams(window.location.search);
      const typeHint = loginParams.get('type');
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential, ...(typeHint ? { userType: typeHint } : {}) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Google sign-in failed');
      const userType = data.userType;
      login(data.token, userType, data.user?.id, data.user?.is_admin);
      if (data.tos_accepted) localStorage.setItem('tos_accepted', 'true');
      if (data.organization) localStorage.setItem('rentual_org', JSON.stringify(data.organization));
      if (redirectTarget) {
        navigate(redirectTarget);
      } else if (data.user?.is_admin) {
        navigate('/admin/dashboard');
      } else if (userType === 'landlord') {
        navigate('/landlord/dashboard/v2');
      } else {
        navigate('/tenant/dashboard');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResendInfo(null);
    setResendStatus(null);
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data?.error && String(data.error).toLowerCase().includes('verify your email')) {
          if (data?.userType) {
            setResendInfo({ email, userType: data.userType });
          }
          throw new Error(data.error || 'Please verify your email before logging in.');
        }
        throw new Error(data?.error || 'Login failed');
      }
      const userType = data.userType;
      const transitionConfig = {
        durationIn: 100,
        hold: 250,
        durationOut: 200
      };
      window.dispatchEvent(new CustomEvent('route-transition', { detail: transitionConfig }));
      setTimeout(() => {
        login(data.token, userType, data.user.id, data.user.is_admin);
        // Store ToS acceptance flag for TosGate
        if (data.tos_accepted) {
          localStorage.setItem('tos_accepted', 'true');
        } else {
          localStorage.removeItem('tos_accepted');
        }
        if (data.organization) {
          localStorage.setItem('rentual_org', JSON.stringify(data.organization));
        }
        if (redirectTarget) {
          navigate(redirectTarget);
        } else if (data.user.is_admin) {
          navigate('/admin/dashboard');
        } else if (userType === 'landlord') {
          navigate('/landlord/dashboard/v2');
        } else {
          navigate('/tenant/dashboard');
        }
      }, transitionConfig.durationIn);
    } catch (err) {
      setError(err.message);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!resendInfo?.email || !resendInfo?.userType) return;
    setResendLoading(true);
    setResendStatus(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/verify-email/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resendInfo.email, userType: resendInfo.userType })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to resend verification email');
      setResendStatus('Verification email sent. Check your inbox.');
    } catch (err) {
      setResendStatus(err.message || 'Unable to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <main className="App-main">
      <PageSEO title="Log In | Rentual" description="Log in to your Rentual account to manage properties, screen tenants, and collect rent." path="/login" />
      <h2 className="login-brand-title">Login to rentual.co</h2>
      <form onSubmit={handleSubmit} className="auth-form">
        {reason === 'expired' && <p className="status-text">Your session expired — please log in again.</p>}
        {error && <p className="error-message">{error}</p>}

        {GOOGLE_CLIENT_ID && (
          <>
            <div style={{
              display: 'flex', justifyContent: 'center', marginBottom: '0.5rem',
              opacity: googleLoading ? 0.6 : 1, pointerEvents: googleLoading ? 'none' : 'auto',
              overflow: 'hidden'
            }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError('Google sign-in failed. Please try again.')}
                text="signin_with"
                shape="pill"
                size="large"
                width="360"
                theme={document.body.getAttribute('data-theme') === 'dark' ? 'filled_black' : 'outline'}
                logo_alignment="center"
              />
            </div>
            {googleLoading && <p className="status-text" style={{ textAlign: 'center', margin: '0.25rem 0 0.5rem', fontSize: '0.85rem' }}>Signing in with Google...</p>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color, #e5e7eb)' }} />
              <span style={{ fontSize: '0.82rem', color: 'var(--light-text-color)', whiteSpace: 'nowrap' }}>or sign in with email</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color, #e5e7eb)' }} />
            </div>
          </>
        )}
        {resendInfo && (
          <div className="form-status" style={{ marginBottom: '0.75rem' }}>
            <p className="status-text">Email not verified. Resend the verification email?</p>
            <button type="button" className="secondary-button" onClick={handleResendVerification} disabled={resendLoading}>
              {resendLoading ? 'Sending...' : 'Resend verification'}
            </button>
            {resendStatus && <p className="status-text" style={{ marginTop: '0.5rem' }}>{resendStatus}</p>}
          </div>
        )}
        <div className="form-group">
          <label htmlFor="email">Login email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={handleEmailBlur}
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={128}
            required
          />
        </div>
        <div className="auth-actions">
          <button type="submit" className="primary-button" disabled={loading}>
            {loading ? 'Signing in…' : 'Login'}
          </button>
        </div>
        <p className="status-text" style={{ marginTop: '0.5rem' }}>
          <Link to="/forgot-password">Forgot your password?</Link>
        </p>
      </form>
      <div className="status-text" style={{ marginTop: '1rem' }}>
        <p><strong>What you get:</strong></p>
        <ul>
          <li>Viewing scheduling + auto-confirmed tours</li>
          <li>Lease renewals + tenant responses</li>
          <li>Maintenance tracking and document vault</li>
        </ul>
      </div>
      <p>Don't have an account? <Link to={redirectTarget ? `/register?redirect=${encodeURIComponent(redirectTarget)}` : '/register'}>Register here</Link></p>
    </main>
  );
}

function LoginWrapper() {
  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <Login />
      </GoogleOAuthProvider>
    );
  }
  return <Login />;
}

export default LoginWrapper;
